import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTrack } from "../hooks/useTrack";
import { TABS } from "../constants/ui";
import { BrandLogo } from "../components/nav/BrandLogo";
import { UserMenu } from "../components/nav/UserMenu";
import { SupportHeader } from "./SupportHeader";
import { LaunchModal } from "../features/campaigns/LaunchModal";
import { BudgetDrawer } from "../features/campaigns/BudgetDrawer";
import { AdvertiserSettingsModal } from "../features/support/AdvertiserSettingsModal";
import { CampaignSettingsApprovalModal } from "../features/support/CampaignSettingsApprovalModal";
import { AdvertiserChangeHistoryModal } from "../features/support/AdvertiserChangeHistoryModal";
import { ExportsPanel } from "../features/exports/ExportsPanel";
import { supabase } from "../lib/supabase";
import { PageBackground } from "../components/PageBackground";
import { AnnouncementBanner } from "../components/AnnouncementBanner";
import { Toast, useToast } from "../components/Toast";
import { ScreenEnter } from "../components/motion/ScreenEnter";

// URL → tab index mapping. Must stay in lock-step with TABS in src/constants/ui.js.
const TAB_PATHS = ["/", "/creative", "/reports"];

// Route changes animate through ScreenEnter (components/motion).
const TAB_ANIM_STYLES = `
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (prefers-reduced-motion: reduce) {
    @keyframes shimmer {
      0%   { background-position: 0 0; }
      100% { background-position: 0 0; }
    }
  }
`;

// Before a pause (paused_reason null) a strike may be a decline or a missing
// card, so that copy asks for either and says billing is still running.
function autoChargeNoticeBody(pausedReason) {
  if (pausedReason === "no_card") {
    return "Automatic billing is paused because there is no card on file. Add a card to resume it.";
  }
  if (pausedReason) {
    return "Automatic billing is paused because we couldn't charge your card. Update your card details to resume it.";
  }
  return "We couldn't complete your automatic payment. Add a card or check your card details to keep automatic billing running.";
}

export function MainLayout({ gaOpenIntegrations = false, gaErrorFromUrl = false, integrationResult = null, clearIntegrationResult }) {
  const { masqueradeAdvertiserId, effectiveAdvertiserName, effectiveAdvertiserId, clearMasquerade, isSupportUser } = useAuth();
  const track = useTrack();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = Math.max(0, TAB_PATHS.indexOf(pathname));
  const [hoveredTab, setHoveredTab] = useState(null);
  const [showLaunch, setLaunch] = useState(false);
  const [showBudget, setBudget] = useState(false);
  const [showAdvertiserSettings, setAdvertiserSettings] = useState(false);
  const [campaignSettingsRefresh, setCampaignSettingsRefresh] = useState(0);
  const [pendingSettingsRow, setPendingSettingsRow] = useState(null);
  const [showApprovalModal, setApprovalModal] = useState(false);
  const [showChangeHistory, setChangeHistory] = useState(false);
  const [showExportsPanel, setShowExportsPanel] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [autoChargePauseNotice, setAutoChargePauseNotice] = useState(null);
  const [toast, showToast] = useToast();

  // Fetch the single active announcement on mount (belt-and-suspenders alongside RLS).
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    const nowIso = new Date().toISOString();
    supabase
      .from("announcements")
      .select("id, title, body, cta_label, cta_url, text_align")
      .eq("is_active", true)
      .lte("starts_at", nowIso)
      .gte("ends_at", nowIso)
      .order("starts_at", { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        if (localStorage.getItem(`dismissed_announcement_${data.id}`)) return;
        setAnnouncement(data);
      });
    return () => { cancelled = true; };
  }, []);

  const dismissAnnouncement = useCallback(() => {
    setAnnouncement((curr) => {
      if (curr) localStorage.setItem(`dismissed_announcement_${curr.id}`, "1");
      return null;
    });
  }, []);

  // Advertiser-facing half of the auto-charge pause feature (the BE job writes
  // paused_reason/paused_at/failure_count to advertiser_auto_charge_settings —
  // see 050_advertiser_auto_charge_settings.sql; support already sees this in
  // AdvertiserSettingsModal, this surfaces it to the advertiser themselves).
  // Reuses AnnouncementBanner itself, not the announcements table — that table
  // is a global broadcast with no advertiser scoping and no backend write path,
  // a poor fit for a per-advertiser system-generated notice.
  useEffect(() => {
    if (!supabase || !effectiveAdvertiserId) { setAutoChargePauseNotice(null); return; }
    let cancelled = false;
    const id = `auto-charge-notice-${effectiveAdvertiserId}`;
    supabase
      .from("advertiser_auto_charge_settings")
      .select("failure_count, paused_reason")
      .eq("advertiser_id", effectiveAdvertiserId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          // Transient read failure — leave any existing dismissal untouched.
          setAutoChargePauseNotice(null);
          return;
        }
        if (!data?.failure_count) {
          // Healthy (or no row yet) — clear any stale dismissal so a future
          // failure streak isn't pre-suppressed by an old one.
          localStorage.removeItem(`dismissed_${id}`);
          setAutoChargePauseNotice(null);
          return;
        }
        if (localStorage.getItem(`dismissed_${id}`)) { setAutoChargePauseNotice(null); return; }
        setAutoChargePauseNotice({
          id,
          title: "Billing issue detected",
          body: autoChargeNoticeBody(data.paused_reason),
        });
      });
    return () => { cancelled = true; };
  }, [effectiveAdvertiserId]);

  const dismissAutoChargePauseNotice = useCallback(() => {
    setAutoChargePauseNotice((curr) => {
      if (curr) localStorage.setItem(`dismissed_${curr.id}`, "1");
      return null;
    });
  }, []);

  useEffect(() => {
    if (!supabase || !masqueradeAdvertiserId || !isSupportUser) {
      setPendingSettingsRow(null);
      return;
    }
    supabase
      .from("campaign_settings")
      .select("id, status, updated_at")
      .eq("advertiser_id", masqueradeAdvertiserId)
      .eq("status", "pending")
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setPendingSettingsRow(data ?? null));
  }, [masqueradeAdvertiserId, isSupportUser, campaignSettingsRefresh]);

  const openLaunch = useCallback(() => { setLaunch(true); track('campaign_launch_opened'); }, [track]);
  const openBudget = useCallback(() => { setBudget(true); track('budget_drawer_opened'); }, [track]);
  const outletCtx = useMemo(
    () => ({ openLaunch, openBudget, campaignSettingsRefresh }),
    [openLaunch, openBudget, campaignSettingsRefresh],
  );

  // Tools registered in the SupportHeader's "Tools" menu. Authored locally
  // (not in src/app/supportTools.js) so each onSelect closes over this
  // component's own state setters directly — SupportHeader has no ctx slot
  // for Exports. See sdd/affiliate-exporter-internal-only decision 9.
  const supportHeaderTools = useMemo(() => [
    {
      id: "change-history",
      label: "Change history",
      group: "Account",
      isVisible: () => true,
      onSelect: () => setChangeHistory(true),
    },
    {
      id: "exports",
      label: "Affiliate Integration",
      group: "Account",
      isVisible: () => true,
      onSelect: () => setShowExportsPanel(true),
    },
  ], []);

  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      {/* Page background — single blurred border shape */}
      <PageBackground />
      <style>{TAB_ANIM_STYLES}</style>
      <SupportHeader
        masqueradeAdvertiserId={masqueradeAdvertiserId}
        effectiveAdvertiserName={effectiveAdvertiserName}
        isSupportUser={isSupportUser}
        pendingSettingsRow={pendingSettingsRow}
        clearMasquerade={clearMasquerade}
        onOpenAdvertiserSettings={() => setAdvertiserSettings(true)}
        onOpenApprovalModal={() => setApprovalModal(true)}
        onOpenChangeHistory={() => setChangeHistory(true)}
        tools={supportHeaderTools}
      />
      <AnnouncementBanner announcement={announcement} onDismiss={dismissAnnouncement} />
      <AnnouncementBanner announcement={autoChargePauseNotice} onDismiss={dismissAutoChargePauseNotice} dismissLabel="Dismiss billing notice" />
      {/* Nav */}
      <div style={{ background:"rgba(255,255,255,0.85)", backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", borderBottom:"1px solid #dad8d9", padding:"0 30px", display:"flex", alignItems:"center", gap:0, position:"relative", zIndex:30 }}>
        <div style={{ marginRight:30, padding:"10px 0", flexShrink:0 }}><BrandLogo /></div>
        <div style={{ display:"flex", flex:1, minWidth:0, gap:4 }}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => { navigate(TAB_PATHS[i]); track('tab_viewed', { tab: t }); }}
              onMouseEnter={() => setHoveredTab(i)}
              onMouseLeave={() => setHoveredTab(null)}
              style={{
                padding:"18px 10px 18px", background:"transparent", border:"none",
                borderBottom: active===i ? "2px solid #F90091" : "2px solid transparent",
                color: active===i ? "#151515" : hoveredTab===i ? "#3E3E3E" : "#6a696a",
                fontSize:13, fontWeight: active===i ? 500 : 400,
                cursor:"pointer", marginBottom:-1, whiteSpace:"nowrap",
                transition:"color 150ms ease, border-color 150ms ease",
              }}>{t}</button>
          ))}
        </div>
        <UserMenu gaOpenIntegrations={gaOpenIntegrations} gaErrorFromUrl={gaErrorFromUrl} integrationResult={integrationResult} clearIntegrationResult={clearIntegrationResult} />
      </div>

      <ScreenEnter key={pathname} style={{ flex:1, position:"relative", zIndex:20 }}>
        <Outlet context={outletCtx} />
      </ScreenEnter>

      {showLaunch && <LaunchModal onClose={() => setLaunch(false)} />}
      {showBudget && (
        <BudgetDrawer
          onClose={() => setBudget(false)}
          onSaved={(resumeMessage) => {
            setCampaignSettingsRefresh((r) => r + 1);
            if (resumeMessage) showToast(resumeMessage.message, resumeMessage.type);
          }}
        />
      )}
      {showAdvertiserSettings && masqueradeAdvertiserId != null && (
        <AdvertiserSettingsModal
          advertiserId={masqueradeAdvertiserId}
          advertiserName={effectiveAdvertiserName}
          onClose={() => setAdvertiserSettings(false)}
          onSaved={(warning) => { if (warning) showToast(warning, "warning"); }}
        />
      )}
      {showApprovalModal && masqueradeAdvertiserId != null && (
        <CampaignSettingsApprovalModal
          advertiserId={masqueradeAdvertiserId}
          advertiserName={effectiveAdvertiserName}
          onClose={() => setApprovalModal(false)}
          onActioned={(resumeMessage) => {
            setPendingSettingsRow(null);
            setCampaignSettingsRefresh((r) => r + 1);
            if (resumeMessage) showToast(resumeMessage.message, resumeMessage.type);
          }}
        />
      )}

      {showChangeHistory && masqueradeAdvertiserId != null && (
        <AdvertiserChangeHistoryModal
          advertiserId={masqueradeAdvertiserId}
          advertiserName={effectiveAdvertiserName}
          onClose={() => setChangeHistory(false)}
        />
      )}

      {showExportsPanel && masqueradeAdvertiserId != null && (
        <ExportsPanel onClose={() => setShowExportsPanel(false)} />
      )}

      {/* Footer */}
      <div style={{ textAlign:"center", padding:"36px 0 28px", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
        <span style={{ fontSize:18, color:"rgba(98,98,98,0.7)", fontFamily:"'DM Sans',sans-serif" }}>Powered by</span>
        <svg width="126" height="20" viewBox="0 0 973.7 146.9" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity:0.5 }}>
          <path fill="#151515" d="M7.6,59.2c-4.2,0-7.6,3.5-7.6,7.6v16.7c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6v-16.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M174.6,59.2h0c4.2,0,7.6,3.4,7.6,7.6v16.6c0,4.2-3.4,7.6-7.6,7.6h0c-4.2,0-7.6-3.4-7.6-7.6v-16.6c0-4.2,3.4-7.6,7.6-7.6Z"/>
          <path fill="#151515" d="M39.1,34.1c-4.2,0-7.6,3.4-7.6,7.6v67c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V41.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M143,34.1c-4.2,0-7.6,3.4-7.6,7.6v67c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V41.7c0-4.2-3.4-7.6-7.6-7.6Z"/>
          <path fill="#151515" d="M104.9,3.7c-4.1-2.4-8.8-3.7-13.9-3.7s-9.7,1.3-13.8,3.7c-8.4,4.8-14,13.8-14,24.2v111.5c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6v-50.6h25.5v50.6c0,4.2,3.4,7.6,7.6,7.6s7.6-3.4,7.6-7.6V27.9c0-10.4-5.7-19.4-14-24.2h-.2ZM103.8,73.6h-25.5V27.9c0-7,5.7-12.7,12.7-12.7s12.7,5.7,12.7,12.7v45.7h.1Z"/>
          <rect fill="#151515" x="506.6" y="32.4" width="13.7" height="82" rx="6.6" ry="6.6"/>
          <path fill="#151515" d="M390.4,32.4h-.6c-3.6,0-6.6,2.9-6.6,6.6v39.8c0,4.6-2,11.2-5.8,15.3-3.4,3.6-9,6.6-16.5,6.6s-13.2-3.1-16.6-6.6c-3.8-4.1-5.8-9-5.8-15.3v-39.8c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v37.4c0,9.7,1.9,17.8,7.4,24.7,5.3,6.7,16.5,13.2,28.8,13.2s23.3-6.5,28.7-13.2c5.5-6.9,7.4-15,7.4-24.7v-37.4c0-3.6-2.9-6.6-6.6-6.6h0Z"/>
          <path fill="#151515" d="M697,32.5h-.6c-3.6,0-6.6,2.9-6.6,6.6v28.8h-39v-28.8c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v68.8c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6v-26.3h39v26.3c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6V39.1c0-3.6-2.9-6.6-6.6-6.6Z"/>
          <path fill="#151515" d="M277.6,36.2c-1.2-2.4-3.6-3.8-6.2-3.8s-5,1.4-6.2,3.8l-34.2,68.2c-1.7,3.4-.3,7.5,3.1,9.2,3.4,1.7,7.5.3,9.2-3.1,0,0,2.8-5.6,6.5-12.9h43.2c3.7,7.3,6.5,12.9,6.5,12.9,1.7,3.4,5.8,4.8,9.2,3.1h0c3.4-1.7,4.7-5.8,3-9.2l-34.2-68.2h.1ZM256.1,85.2c4.9-9.7,9.5-18.9,10.5-20.9,1.4-2.9,3.6-7.5,4.9-10.4,1.3,2.9,3.4,7.5,4.9,10.4,1,2,5.6,11.2,10.5,20.9h-30.8Z"/>
          <path fill="#151515" d="M577.7,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM577.9,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M759.1,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM759.3,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M850.8,32.5c-24.2,0-39.9,17.3-39.9,41s14.9,41,39.9,41,39.9-19.7,39.9-41-15.6-41-39.9-41ZM851,100.5c-16.2,0-25.8-13-25.8-27.1s10.1-27.1,25.8-27.1,25.8,11.4,25.8,27.1-9.6,27.1-25.8,27.1Z"/>
          <path fill="#151515" d="M971.8,102.8l-31.5-32.7,28.5-25.7c2.8-2.5,3-6.9.5-9.7s-6.9-3-9.7-.5l-27.3,24.6c-2,1.8-7.1,6.8-11.3,10.9v-30.7c0-3.6-2.9-6.6-6.6-6.6h-.6c-3.6,0-6.6,2.9-6.6,6.6v68.8c0,3.6,2.9,6.6,6.6,6.6h.6c3.6,0,6.6-2.9,6.6-6.6v-20.4l9.4-8.4c9.3,9.9,31.6,33.3,31.6,33.3,2.6,2.7,7,2.8,9.7.2,2.7-2.6,2.8-7,.2-9.7h-.1Z"/>
          <path fill="#151515" d="M448.5,34.4h-23.9c-4.3,0-7.8,3.5-7.8,7.8v62.5c0,4.3,3.5,7.8,7.8,7.8h23.9c7.3,0,20.4-.5,29.9-14.2,4-5.8,8-13.2,8-24.8s-4-19-8-24.8c-9.5-13.7-22.6-14.2-29.9-14.2h0ZM468.6,88.5c-3.4,5.9-8.6,11.3-19,11.3h-19.1v-52.8h19.1c10.4,0,15.6,5.4,19,11.3,3.2,5.6,4,10,4,15s-.8,9.4-4,15v.2Z"/>
        </svg>
      </div>

      <Toast {...toast} />
    </div>
  );
}
